import APP_CONFIG from '@/app.config';
import Cookies from '@/utils/common/cookies';
import { hasEveryFlag } from '@/utils/common/feature-flag';
import { chimeraQuery } from '@/utils/services/chimera';
import SentryClient from '@/utils/services/sentry';

import DexUserService, { DexUser } from './dex-user.model';
import { meUserQuery, userProfileQuery } from './user.queries';

export type ChimeraUserProfile = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  picture: string;
  pictureSHA: string;
  isUnfitPhoto: boolean;
};

type ChimeraMeUserRole = {
  id: string;
  name: string;
};

export type ChimeraMeUser = {
  id: string;
  name: string;
  email: string;
  picture: string;
  roles: ChimeraMeUserRole[];
  featureFlags: any;
};

class User {
  static getUserToken(): string | undefined {
    const d = Cookies.getItem('d', false);
    return d;
  }

  static getFallbackUser(): ChimeraUserProfile {
    return {
      id: '-1',
      firstName: 'Usuario',
      lastName: 'Desconhecido',
      email: 'Email Desconhecido',
      picture: 'https://i.pravatar.cc/300',
      pictureSHA: '',
      isUnfitPhoto: false,
    };
  }

  static async fetchUserProfile(userEmail: string): Promise<ChimeraUserProfile | undefined> {
    try {
      const response = await chimeraQuery(userProfileQuery, { userEmail });
      const { userProfile } = response.data.chimera;
      return userProfile;
    } catch (error) {
      const reportError =
        error instanceof Error ? error : new Error('Could not fetch user profile');
      SentryClient.captureError(reportError);
      return undefined;
    }
  }

  static async fetchMeUser(): Promise<ChimeraMeUser | undefined> {
    try {
      const response = await chimeraQuery(meUserQuery);
      const { me } = response.data.chimera;
      return me;
    } catch (error) {
      const reportError = error instanceof Error ? error : new Error('Could not fetch Me user');
      SentryClient.captureError(reportError);
      return undefined;
    }
  }

  static hasCheckDexRole(user: DexUser | undefined, requiredRole: string): boolean {
    return !!(user && user.email && user.role && user.role === requiredRole);
  }

  static hasSomeEmailAndRole(user: ChimeraMeUser | undefined): boolean {
    return !!(user && user.email && user.roles && user.roles.length > 0);
  }

  static hasSomeRole(user: ChimeraMeUser | undefined, requiredRoles: string[]): boolean {
    const requiredRoleSet = new Set(requiredRoles);
    return !!user && user.roles.some((userRole) => requiredRoleSet.has(userRole.name));
  }

  static hasEveryRole(user: ChimeraMeUser | undefined, requiredRoles: string[]): boolean {
    const requiredRoleSet = new Set(requiredRoles);
    return !!user && user.roles.every((userRole) => requiredRoleSet.has(userRole.name));
  }

  static getJoinedRoles(user: ChimeraMeUser | undefined): string {
    return user && user.roles.length > 0 ? user.roles.map((role: any) => role.name).join(', ') : '';
  }

  static getDefaultMeUser(): ChimeraMeUser {
    return {
      id: '1',
      name: 'Colaborador do Descomplica',
      email: 'colaborador@descomplica.com.br',
      picture: 'https://source.unsplash.com/random',
      roles: [],
      featureFlags: [],
    };
  }

  static hasFeatureFlag(user: ChimeraMeUser | undefined, key: string): boolean {
    return user?.featureFlags[key];
  }

  static getFirstName(storedUser: ChimeraMeUser | undefined): string | undefined | null {
    const userName = storedUser?.name || APP_CONFIG.conventions.DEFAULT_USER_NAME;
    return userName && userName.split(' ')[0];
  }

  static async authenticate(onFail: () => void): Promise<ChimeraMeUser> {
    const authError = new Error('Could not authenticate user');
    const d = User.getUserToken();
    if (!d) {
      onFail();
      throw authError;
    }

    const fetchedUser = await User.fetchMeUser();
    if (!fetchedUser) throw authError;

    return fetchedUser;
  }

  static authorizeChimera(user: ChimeraMeUser | undefined, onFail: () => void): boolean {
    if (
      !user ||
      !User.hasSomeEmailAndRole(user) ||
      !hasEveryFlag(user, ['undergrad-alpha-testing'])) 
    {
      onFail();
      return false;
    }

    return true;
  }

  static authorizeDex(userDex: DexUser | undefined, onFail: () => void): boolean {
    if (
      !userDex ||
      !User.hasCheckDexRole(userDex,'admin')) 
    {
      onFail();
      return false;
    }

    return true;
  }

  static async dexAuthenticate(): Promise<DexUser> {
    const fetchedUser = await DexUserService.fetchDexUser();
    if (!fetchedUser) throw new Error('Could not authenticate dex user');

    return fetchedUser;
  }
}

export default User;
